import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import './i18n';
import './index.css';
import './styles/modern-theme.css';
import './styles/dark-mode.css';
import './styles/landing-page.css';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, type Configuration } from '@azure/msal-browser';
import { FluentProvider } from '@fluentui/react-components';
import { AuthProvider } from './contexts/AuthContext';
import { RoleProvider } from './contexts/RoleContext';
import { FeaturesProvider } from './contexts/FeaturesContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { logger } from './lib/logger';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { resolveLoginClientId } from './auth/appRegistration';
import {
  initializeMsalAuthentication,
  formatMsalError,
  MSAL_POPUP_REDIRECT_PATH,
  getMsalPopupRedirectUri,
} from './auth/msal';
import { teamsAuth } from './lib/teams-auth';

logger.debug('main.tsx loading', undefined, 'INIT');

const loginClientId = resolveLoginClientId(
  import.meta.env.VITE_EXOBE_ID,
  import.meta.env.VITE_AZURE_CLIENT_ID
);

// Every window MSAL drives — the sign-in popup, the hidden iframe used for silent
// token renewal, the logout popup — must land on the dedicated callback route, not
// on the app itself. msal-browser v5 completes those flows over a BroadcastChannel
// that only the redirect bridge publishes to (see bootstrapApplication below), so a
// redirect URI pointing at the SPA leaves the waiting frame to time out.
const msalConfig: Configuration = {
  auth: {
    clientId: loginClientId,
    authority: 'https://login.microsoftonline.com/organizations',
    redirectUri: getMsalPopupRedirectUri(),
    postLogoutRedirectUri: getMsalPopupRedirectUri(),
  },
  // localStorage (shared by every MSAL instance here, see lib/graph.ts) on purpose: sessionStorage would
  // mean a sign-in per tab and is fragile in the Teams and Outlook hosts. The token-theft risk this
  // carries is held down by the CSP in public/staticwebapp.config.json, which allows no inline, eval'd
  // or arbitrary-host script; keep it that way.
  cache: { cacheLocation: 'localStorage' }
};

// Global error handlers.
//
// These replace the whole document with a debug screen, which is useful locally and
// actively harmful in production: any stray error — a rejected promise from a
// third-party script, a flaky network call — wiped the running app. Inside an M365
// host webview (Outlook/Microsoft 365 mobile) that reads as "the app never loaded".
// Log everywhere; only take over the page in development.
if (import.meta.env.DEV) {
  window.addEventListener('error', (event) => {
    // ResizeObserver loop errors are benign browser notifications, not actual bugs.
    // We skip our custom overlay but let other handlers and default logging run.
    if (event.message?.includes('ResizeObserver loop')) {
      return;
    }
    document.body.innerHTML = `<div style="padding: 20px; background: orange; color: black; font-family: monospace; z-index: 9999; position: fixed; top: 0; left: 0; right: 0; bottom: 0;">
      <h1>Global JavaScript Error</h1>
      <p><strong>Message:</strong> ${event.message}</p>
      <p><strong>Source:</strong> ${event.filename}:${event.lineno}:${event.colno}</p>
      <pre>${event.error?.stack || 'No stack trace'}</pre>
    </div>`;
  });

  window.addEventListener('unhandledrejection', (event) => {
    document.body.innerHTML = `<div style="padding: 20px; background: purple; color: white; font-family: monospace; z-index: 9999; position: fixed; top: 0; left: 0; right: 0; bottom: 0;">
      <h1>Unhandled Promise Rejection</h1>
      <pre>${event.reason}</pre>
    </div>`;
  });
} else {
  window.addEventListener('error', (event) => {
    if (event.message?.includes('ResizeObserver loop')) {
      return;
    }
    logger.error('Uncaught error', {
      message: event.message,
      source: `${event.filename}:${event.lineno}:${event.colno}`,
      stack: event.error?.stack,
    }, 'INIT');
  });

  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', { reason: String(event.reason) }, 'INIT');
  });
}

logger.debug('MSAL Config', {
  clientId: loginClientId,
  exobeId: import.meta.env.VITE_EXOBE_ID,
  redirectUri: window.location.origin + '/',
}, 'INIT');

const pca = new PublicClientApplication(msalConfig);

// Wrapper component to use theme context
function AppWithTheme() {
  const { theme } = useTheme();
  
  return (
    <FluentProvider theme={theme}>
      <App />
    </FluentProvider>
  );
}

function renderApplication() {
  try {
    logger.debug('Starting React app render', undefined, 'INIT');
    
    const rootElement = document.getElementById('root');
    logger.debug('Root element found', { hasRoot: !!rootElement }, 'INIT');
    
    ReactDOM.createRoot(rootElement!).render(
      <React.StrictMode>
        <ErrorBoundary>
          <BrowserRouter>
            <MsalProvider instance={pca}>
              <AuthProvider>
                <LanguageProvider>
                  <FeaturesProvider>
                    <RoleProvider>
                      <ThemeProvider>
                        <AppWithTheme />
                      </ThemeProvider>
                    </RoleProvider>
                  </FeaturesProvider>
                </LanguageProvider>
              </AuthProvider>
            </MsalProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </React.StrictMode>
    );
    logger.debug('React app render completed', undefined, 'INIT');
    
    // Service worker: deliberately NOT registered.
    //
    // This was previously "temporarily disabled for debugging" and shipped that way.
    // Making it a decision rather than a leftover: the app has no offline story, and a
    // shell-caching service worker inside an M365 host iframe risks serving stale
    // assets to Teams/Outlook/Microsoft 365 users after a deploy — the hosts keep
    // webviews alive far longer than a browser tab. We keep calling unregister() so
    // any worker installed by an earlier build is torn down on next load.
    serviceWorkerRegistration.unregister();
    logger.debug('Service worker not registered (unregistering any previous worker)', undefined, 'INIT');
  } catch (error) {
    logger.error('Failed to render React app', error, 'INIT');
    // Take the host's shimmer down so the message below is actually visible
    // rather than hidden behind an indicator that never clears.
    teamsAuth.releaseLoadingIndicator(false);
    document.body.innerHTML = `<div style="padding: 20px; background: red; color: white; font-family: monospace; z-index: 9999; position: fixed; top: 0; left: 0; right: 0; bottom: 0;">
      <h1>Error rendering React app</h1>
      <pre>${error}</pre>
      <pre>${error instanceof Error ? error.stack : 'No stack trace'}</pre>
    </div>`;
  }
}

async function bootstrapApplication(): Promise<void> {
  // This window is the MSAL auth callback: the sign-in/logout popup, or the hidden
  // iframe MSAL uses to renew tokens silently. The SWA fallback serves index.html
  // for MSAL_POPUP_REDIRECT_PATH, so the SPA starts here — but it must not run the
  // app, and it must not call handleRedirectPromise().
  //
  // msal-browser v5 does not read this window's URL from the outside. The frame that
  // started the flow parks on a BroadcastChannel named after the request's state id
  // and waits (system.popupBridgeTimeout / iframeBridgeTimeout), and publishing the
  // raw response onto that channel is this page's job — that is what the redirect
  // bridge does. handleRedirectPromise is the *main frame* API for the redirect flow;
  // it never broadcasts, which is why the popup used to sit on the callback URL with
  // the response still in the fragment while sign-in quietly timed out.
  //
  // broadcastResponseToMainFrame() posts the response and closes this window itself
  // (for a redirect-flow response it caches the payload and navigates home instead).
  if (window.location.pathname === MSAL_POPUP_REDIRECT_PATH) {
    logger.debug('MSAL auth callback – handing the response to the redirect bridge', undefined, 'AUTH');
    try {
      const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');
      await broadcastResponseToMainFrame();
    } catch (error) {
      // No usable response in the URL: a stray direct navigation, or a malformed
      // callback. Never fall through to the app — in a popup or iframe that renders
      // a second copy of QuickDial inside a transient window.
      logger.error('MSAL redirect bridge failed', formatMsalError(error), 'AUTH');
      if (window.opener || window.parent !== window) {
        window.close();
      } else {
        window.location.replace('/');
      }
    }
    return;
  }

  // Start the M365 host handshake before anything else, and deliberately do NOT
  // await it.
  //
  // `teamsAuth.initializeContext()` is what dismisses the host's native loading
  // indicator (manifest `showLoadingIndicator: true`), and until this ran it was
  // reached only from an effect inside AuthProvider — i.e. only if MSAL bootstrap
  // succeeded AND the whole React tree rendered without throwing. Anything that
  // failed earlier left Outlook and the Microsoft 365 app sitting behind their
  // shimmer with no timeout, which is indistinguishable from "the app never
  // loaded". That is precisely how the iOS-only failure presented: a storage
  // SecurityError in the WKWebView took the first render down, so the notify
  // never happened, while the same build worked on Android.
  //
  // Kicking it off here makes dismissal depend on nothing but the SDK. It is
  // idempotent — AuthProvider awaits this same in-flight promise rather than
  // starting a second handshake.
  void teamsAuth.initializeContext().catch((error) => {
    logger.error('M365 host detection failed', error, 'INIT');
    teamsAuth.releaseLoadingIndicator(false);
  });

  try {
    await initializeMsalAuthentication(pca);
  } catch (error) {
    // Non-fatal. MSAL backs the browser sign-in flow only; inside an M365 host
    // the app authenticates via host SSO and never touches it. Blanking the page
    // here used to turn a recoverable MSAL problem into a dead tab, so log it and
    // render regardless — the UI can still show a real error to the user.
    logger.error('MSAL initialization failed — continuing without it', formatMsalError(error), 'INIT');
  }

  renderApplication();
}

void bootstrapApplication().catch((error) => {
  logger.error('Unexpected MSAL bootstrap failure', error, 'INIT');
});
